import { gql } from '@apollo/client';

// dev point : look at the query in with type defs (id: String!)
export const GET_COACH = gql`
  query GetCoach {
    getCoach {
      id
      firstName
      lastName
      profilePic
      email
      surname
      timeZone
      language
      pronoun
      dob
      tp
      isActive
      status
      isAgreedPrivacyPolicy
      subscribeToNewsletter
      isExternalImage
      subscriptionPlan {
        name
        maxUserCount
      }
      previousPlan
      userGroup {
        modules {
          UserPermissions {
            permissionCode
          }
        }
      }
      userStatusType
      paymentStatus
      previousPlan
      subscriptionPlanName
      subscriptionPlanStatus {
        daysLeft
        currentPlan
        paymentStatus
        nextPlan
      }
      isReadOnly
      brandTag
      clientCount
      clientCountStatus
      stripeStatus
      role
      isPrincipalCoach
    }
  }
`;

export const GET_ORGANISATION_COACHES = gql`
  query GetOrganisationCoaches(
    $organizationId: String!
    $take: Int!
    $skip: Int!
    $nameText: String
    $status: CoachStatus
    $isArchived: Boolean
    $brandTag: String
  ) {
    getOrganizationCoaches(
      organizationId: $organizationId
      take: $take
      skip: $skip
      nameText: $nameText
      status: $status
      isArchived: $isArchived
      brandTag: $brandTag
    ) {
      totalClientCount
      coaches {
        id
        firstName
        lastName
        profilePic
        surname
        email
        tp
        status
        country
        isActive
        archived
        clientCount
        activeClients
        inactiveClients
        archivedClients
        brandTag
        lastSeanTime
        subscriptionPlanName
        coachTypes {
          coachType
          description
        }
      }
    }
  }
`;

export const APPROVE_COACH = gql`
  mutation approveCoach($coachId: String!) {
    approveCoach(coachId: $coachId) {
      id
      firstName
      lastName
      email
      status
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_COACH_LANGUAGE_MUTATION = gql`
  mutation updateCoachLanguage($coachId: String!, $lang: Languages!) {
    updateCoachLanguage(coachId: $coachId, lang: $lang) {
      id
      language
    }
  }
`;

export const CREATE_COACH_MUTATION = gql`
  mutation createCoach($CreateCoach: CreateCoach!) {
    createCoach(CreateCoach: $CreateCoach) {
      firstName
      lastName
      email
      dob
      pronoun
      organizationId
      archived
      addressLine1
      addressLine2
      profilePic
      tp
      timeZone
      coachType
      description
    }
  }
`;

export const ONBOARD_COACH_MUTATION = gql`
  mutation createCoach($CreateCoach: CreateCoach!) {
    createCoach(CreateCoach: $CreateCoach) {
      id
      coachTypes
      description
      profilePicLink {
        contentType
        fileName
        url
        key
      }
    }
  }
`;

export const REJECT_COACH = gql`
  mutation rejectCoach($coachId: String!) {
    rejectCoach(coachId: $coachId) {
      id
    }
  }
`;

export const UPDATE_COACH_STATUS_MUTATION = gql`
  mutation ($coachId: String!, $status: CoachCurrentStatus!) {
    changeCoachStatus(UpdateCoachStatus: { coachId: $coachId, status: $status }) {
      id
      isActive
    }
  }
`;

export const ARCHIVE_COACH_MUTATION = gql`
  mutation archiveCoach($archiveCoachInput: ArchiveCoachInput!) {
    archiveCoach(archiveCoachInput: $archiveCoachInput) {
      success
      message
      statusCode
    }
  }
`;

export const UPDATE_COACH = gql`
  mutation UpdateCoach(
    $id: String!
    $firstName: String!
    $lastName: String!
    $timeZone: String!
    $dob: DateTime
    $tp: String
    $pronoun: String
  ) {
    updateCoach(
      UpdateCoach: {
        id: $id
        firstName: $firstName
        lastName: $lastName
        timeZone: $timeZone
        dob: $dob
        tp: $tp
        pronoun: $pronoun
      }
    ) {
      id
      firstName
      lastName
      email
      dob
      tp
      timeZone
      profilePic
      pronoun
    }
  }
`;

export const UPDATE_COACH_PROFILE_PICTURE = gql`
  mutation UpdateCoachProfilePic($id: String!, $contentType: String!, $fileName: String!) {
    updateCoachProfilePic(
      UpdateProfilePic: { id: $id, profilePic: { contentType: $contentType, fileName: $fileName } }
    ) {
      profilePic
      profilePicLink {
        contentType
        url
        fileName
        key
      }
    }
  }
`;

export const GET_COACH_SETTINGS = gql`
  query GetCoachSettings($coachId: String!) {
    getCoachSettings(coachId: $coachId) {
      id
      isNoteTakingAIEnabled
      isMagicSuggestionEnabled
      noteSummaryAIModel
      magicSuggestionAIModel
    }
  }
`;

export const UPDATE_COACH_SETTINGS = gql`
  mutation UpdateCoachSettings($coachSettingsInput: CoachSettingsUpdateInput!) {
    updateCoachSettings(CoachSettingsInput: $coachSettingsInput) {
      isNoteTakingAIEnabled
      isMagicSuggestionEnabled
      noteSummaryAIModel
      magicSuggestionAIModel
    }
  }
`;

export const COACH_ONBOARDING_MUTATION = gql`
  mutation coachOnboarding($CoachOnboarding: CoachOnboarding!) {
    coachOnboarding(CoachOnboarding: $CoachOnboarding) {
      id
      coachTypes {
        coachType
        description
      }
      description
      email
      status
      isPrincipalCoach
    }
  }
`;

export const COACH_DATA_REQUEST = gql`
  mutation createCoachDataRequest($id: String!) {
    createCoachDataRequest(id: $id) {
      message
      success
      statusCode
    }
  }
`;

export const COACH_DELETE_REQUEST = gql`
  mutation requestCoachDeleteAccount($requestCoachDeleteAccount: RequestCoachDeleteAccount!) {
    requestCoachDeleteAccount(RequestCoachDeleteAccount: $requestCoachDeleteAccount) {
      id
    }
  }
`;

export const SUBSCRIBE_TO_CLIENT_COUNT = gql`
  subscription SubscribeToClientsCount($coachId: String!) {
    getClientCountStatus(coachId: $coachId) {
      clientCountStatus
      count
      plan
    }
  }
`;

export const GET_COACH_SERVICE_PACKAGES = gql`
  query getCoachServicePackages($coachId: String!) {
    getCoachServicePackages(coachId: $coachId) {
      servicePackages {
        id
        title
        sessionPrice
        sessionDuration
        sessionDescription
        createdAt
        updatedAt
        coachId
        bookingType
        hasBookings
      }
    }
  }
`;

export const CREATE_COACH_SERVICE_PACKAGE = gql`
  mutation createServicePackage($CreateServicePackage: createServicePackageInput!) {
    createServicePackage(CreateServicePackage: $CreateServicePackage) {
      id
      title
      sessionPrice
      sessionDuration
      sessionDescription
      createdAt
      updatedAt
      coachId
      bookingType
    }
  }
`;

export const UPDATE_COACH_SERVICE_PACKAGE = gql`
  mutation updateServicePackage($UpdateServicePackage: updateServicePackageInput!) {
    updateServicePackage(UpdateServicePackage: $UpdateServicePackage) {
      id
      title
      sessionPrice
      sessionDuration
      sessionDescription
      createdAt
      updatedAt
      coachId
      bookingType
    }
  }
`;

export const DELETE_COACH_SERVICE_PACKAGE = gql`
  mutation deleteServicePackage($DeleteServicePackage: String!) {
    deleteServicePackage(DeleteServicePackage: $DeleteServicePackage) {
      message
      success
      statusCode
    }
  }
`;

export const GET_COACH_EVENT_DETAILS = gql`
  query GetCoachEventDetails($coachId: String!) {
    getCoachEventSettings(coachId: $coachId) {
      id
      coachId
      bufferTime
      minimumNoticeTime
      dayLimit
    }
  }
`;

export const UPDATE_COACH_EVENT_DETAILS = gql`
  mutation UpdateCoachEventSettings(
    $updateCoachEventSettingsInput: UpdateCoachEventSettingsInput!
  ) {
    updateCoachEventSettings(updateCoachEventSettingsInput: $updateCoachEventSettingsInput) {
      id
      coachId
      bufferTime
      minimumNoticeTime
      dayLimit
    }
  }
`;

export const GET_BRAND_CONFIGURATIONS = gql`
  query GetBrandConfigurations($brandTag: String!) {
    getBrandConfigurations(brandTag: $brandTag) {
      id
      brandTag
      brandName
      logo
      brandNameLogo
    }
  }
`;

export const COACH_DELETE_REQUEST_VALIDATION = gql`
  query deleteRequestValidation {
    deleteRequestValidation
  }
`;

export default GET_COACH;
